import * as yup from "yup";

import {
  CreateTreatyEndorsement,
  CreateTreatyEndorsementVariables,
} from "_graphql/mutation/__generated__/CreateTreatyEndorsement";

import { Modal } from "components";
import { TreatyEndorsementForm } from "components/forms";
import _ from "lodash";
import { createTreatyEndorsement } from "_graphql/visal/mutations/treaty";
import toast from "react-hot-toast";
import { useAppLocation } from "_graphql/cache/auth";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { useTreaty } from "hooks/data/use-treaties";
import { wrapClick } from "utils";

const formSchema = yup.object().shape({
  treaty_id: yup.string().required("Treaty ID is required"),
  treaty_endorsement_comment: yup
    .string()
    .required("Endorsement comment is required"),
});

type Props = {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
};

const CreateTreatyEndorsementContainer = ({ open, setOpen }: Props) => {
  const { params } = useAppLocation();
  const { loading: fetchingTreaty, treaty } = useTreaty({
    treatyId: params?.treaty,
  });

  const [create, { loading }] = useMutation<
    CreateTreatyEndorsement,
    CreateTreatyEndorsementVariables
  >(createTreatyEndorsement, {
    refetchQueries: ["GetTreatyEndorsements", "Treaty"],
  });
  const form = useFormik({
    initialValues: {
      treaty_id: params?.treaty || "",
      treaty_endorsement_comment: "",
      treaty_program: "",
      cedant: "",
      reference: "",
      period: "",
      currency: "",
      wth: 0,
      commission: 0,
      brokerage: 0,
    },
    validationSchema: formSchema,
    onSubmit: (values) => {
      create({
        variables: {
          treatyId: values.treaty_id,
          treatyEndorsementComment: values.treaty_endorsement_comment,
        },
      })
        .then(({ data }) => {
          if (data?.createTreatyEndorsement) {
            setOpen(false);
            form.resetForm();
            toast.success("Treaty endorsement created successfully");
            // Optionally, you can refetch the treaty endorsements or show a success message
          } else {
            toast.error("Failed to create treaty endorsement");
          }
        })
        .catch((error) => {
          toast.error("Error creating treaty endorsement");
        });
    },
  });

  useEffect(() => {
    if (!treaty) return;
    form.setValues({
      // treaty_id: treaty.treaty_id,
      treaty_endorsement_comment: "",
      treaty_program: treaty.treaty_program?.treaty_name || "",
      cedant: treaty.insurer?.insurer_company_name || "",
      reference: treaty.treaty_reference || "",
      period:
        `${treaty.treaty_deduction?.treaty_period_from} to ${treaty.treaty_deduction?.treaty_period_to}` ||
        "",
      currency: treaty.currency || "",
      wth: treaty.treaty_deduction?.withholding_tax || 0,
      commission: treaty.treaty_deduction?.commission || 0,
      brokerage: treaty.treaty_deduction?.brokerage || 0,
      treaty_id: params?.treaty || "",
    });
  }, [treaty]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={fetchingTreaty}
      title="Create Treaty Endorsement"
      description="Create a new treaty endorsement"
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Please wait ..." : "Create Treaty Endorsement"}
          </button>
        </>
      )}
    >
      <TreatyEndorsementForm form={form} />
    </Modal>
  );
};

export default CreateTreatyEndorsementContainer;
